import { useEffect, useMemo, useState } from "react";
import api from "api/axiosInstance";
import styles from "../AdminPanel.module.scss";
import {
  DEFAULT_LEGAL_INFO_APPOINTMENT_BODY,
  DEFAULT_LEGAL_INFO_APPOINTMENT_TITLE,
  DEFAULT_LEGAL_INFO_LEAD,
  DEFAULT_LEGAL_INFO_LINKS,
  DEFAULT_LEGAL_INFO_PATIENT_BODY,
  DEFAULT_LEGAL_INFO_PATIENT_TITLE,
  DEFAULT_LEGAL_INFO_TITLE,
  fillLegalTemplate,
  LEGAL_INFO_APPOINTMENT_BODY_KEY,
  LEGAL_INFO_APPOINTMENT_TITLE_KEY,
  LEGAL_INFO_LEAD_KEY,
  LEGAL_INFO_LINKS_KEY,
  LEGAL_INFO_PATIENT_BODY_KEY,
  LEGAL_INFO_PATIENT_TITLE_KEY,
  LEGAL_INFO_TITLE_KEY,
  parseLegalTextBlocks,
  type LegalLinkItem,
} from "../../../../app/legalInfo";

type ClinicInfoRow = {
  id: number;
  name: string;
  detail: string;
};

type DocumentRow = {
  id?: string;
  originalName?: string;
  fileName?: string;
  mimeType?: string;
  size?: number;
  path?: string;
  createdAt?: string;
};

const STATIC_FRONT_DOCUMENT_LINKS: LegalLinkItem[] = [
  {
    label: "Уведомление",
    href: "/documents/Уведомление_ООО_Ю-ДЕНТ.pdf",
    download: true,
  },
  {
    label: "Выписка из реестра",
    href: "/documents/Выписка_из_реестра_-ООО_Ю-ДЕНТ.pdf",
    download: true,
  },
];

function extractErr(e: any) {
  const status = e?.response?.status;
  const data = e?.response?.data;

  const msg =
    typeof data === "string"
      ? data
      : data?.message
      ? Array.isArray(data.message)
        ? data.message.join(", ")
        : String(data.message)
      : data?.error
      ? String(data.error)
      : e?.message;

  return status ? `${msg} (HTTP ${status})` : msg || "Неизвестная ошибка";
}

function safeText(v: unknown) {
  return typeof v === "string" ? v : "";
}

function safeTrim(v: unknown) {
  return safeText(v).trim();
}

function normalizeLink(item: Partial<LegalLinkItem>): LegalLinkItem {
  return {
    label: safeTrim(item?.label),
    href: safeTrim(item?.href),
    download: Boolean(item?.download),
  };
}

function dedupeLinks(list: Array<Partial<LegalLinkItem>>) {
  const seen = new Set<string>();
  const result: LegalLinkItem[] = [];

  for (const raw of list) {
    const item = normalizeLink(raw);
    if (!item.label || !item.href) continue;
    if (seen.has(item.href)) continue;

    seen.add(item.href);
    result.push(item);
  }

  return result;
}

function buildResetLinks() {
  return dedupeLinks([...STATIC_FRONT_DOCUMENT_LINKS, ...DEFAULT_LEGAL_INFO_LINKS]);
}

function parseLinks(raw?: string): LegalLinkItem[] {
  if (!raw?.trim()) {
    return buildResetLinks();
  }

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return buildResetLinks();
    }

    const normalized = parsed
      .map((item) => ({
        label: safeTrim(item?.label),
        href: safeTrim(item?.href),
        download: Boolean(item?.download),
      }))
      .filter((item) => item.label && item.href);

    return normalized.length ? dedupeLinks(normalized) : buildResetLinks();
  } catch {
    return buildResetLinks();
  }
}

function formatBytes(bytes?: number) {
  const size = Number(bytes || 0);
  if (!Number.isFinite(size) || size <= 0) return "0 Б";
  if (size < 1024) return `${size} Б`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} КБ`;
  return `${(size / (1024 * 1024)).toFixed(2)} МБ`;
}

function getDocId(doc: DocumentRow) {
  return safeTrim(doc?.id);
}

function getDocName(doc: DocumentRow) {
  return safeTrim(doc?.originalName) || safeTrim(doc?.fileName) || "";
}

function getDocMime(doc: DocumentRow) {
  return safeTrim(doc?.mimeType) || "application/pdf";
}

function getDocPath(doc: DocumentRow) {
  return safeTrim(doc?.path);
}

function isValidDocument(doc: DocumentRow) {
  return Boolean(getDocId(doc) && getDocName(doc));
}

function normalizeDocuments(list: DocumentRow[]) {
  const seen = new Set<string>();
  const result: DocumentRow[] = [];

  for (const doc of list || []) {
    if (!isValidDocument(doc)) continue;

    const id = getDocId(doc);
    if (!id) continue;
    if (seen.has(id)) continue;

    seen.add(id);

    result.push({
      id,
      originalName: getDocName(doc),
      fileName: safeTrim(doc?.fileName),
      mimeType: getDocMime(doc),
      size: Number(doc?.size || 0),
      path: getDocPath(doc),
      createdAt: safeTrim(doc?.createdAt),
    });
  }

  return result;
}

function getPdfIdFromHref(href?: string) {
  const raw = safeTrim(href);
  const match = raw.match(/^\/documents\/download\/(.+)$/);
  return match?.[1] || "";
}

export default function LegalInfoSettings() {
  const [rows, setRows] = useState<ClinicInfoRow[]>([]);
  const [documents, setDocuments] = useState<DocumentRow[]>([]);

  const [title, setTitle] = useState(DEFAULT_LEGAL_INFO_TITLE);
  const [lead, setLead] = useState(DEFAULT_LEGAL_INFO_LEAD);
  const [patientTitle, setPatientTitle] = useState(DEFAULT_LEGAL_INFO_PATIENT_TITLE);
  const [patientBody, setPatientBody] = useState(DEFAULT_LEGAL_INFO_PATIENT_BODY);
  const [appointmentTitle, setAppointmentTitle] = useState(DEFAULT_LEGAL_INFO_APPOINTMENT_TITLE);
  const [appointmentBody, setAppointmentBody] = useState(DEFAULT_LEGAL_INFO_APPOINTMENT_BODY);
  const [links, setLinks] = useState<LegalLinkItem[]>(buildResetLinks());

  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const [renameMap, setRenameMap] = useState<Record<string, string>>({});
  const [pdfExpanded, setPdfExpanded] = useState(false);

  const rowsByKey = useMemo(() => {
    const map = new Map<string, ClinicInfoRow>();
    rows.forEach((row) => map.set(row.name, row));
    return map;
  }, [rows]);

  const validDocuments = useMemo(() => normalizeDocuments(documents), [documents]);

  /**
   * editableLinks — список для формы редактирования.
   * Тут пустые элементы НЕ фильтруются, чтобы новая кнопка сразу появлялась в UI.
   */
  const editableLinks = useMemo(() => {
    return (links || []).map((item) => ({
      label: safeText(item?.label),
      href: safeText(item?.href),
      download: Boolean(item?.download),
    }));
  }, [links]);

  /**
   * normalizedLinks — очищенный список только для предпросмотра / сохранения.
   */
  const normalizedLinks = useMemo(() => {
    return dedupeLinks(editableLinks);
  }, [editableLinks]);

  const loadClinicInfo = async () => {
    const clinicRes = await api.get<ClinicInfoRow[]>("/clinic-info");
    const loaded = Array.isArray(clinicRes.data) ? clinicRes.data : [];

    setRows(loaded);

    setTitle(
      (loaded.find((x) => x.name === LEGAL_INFO_TITLE_KEY)?.detail || "").trim() ||
        DEFAULT_LEGAL_INFO_TITLE
    );

    setLead(
      (loaded.find((x) => x.name === LEGAL_INFO_LEAD_KEY)?.detail || "").trim() ||
        DEFAULT_LEGAL_INFO_LEAD
    );

    setPatientTitle(
      (loaded.find((x) => x.name === LEGAL_INFO_PATIENT_TITLE_KEY)?.detail || "").trim() ||
        DEFAULT_LEGAL_INFO_PATIENT_TITLE
    );

    setPatientBody(
      (loaded.find((x) => x.name === LEGAL_INFO_PATIENT_BODY_KEY)?.detail || "").trim() ||
        DEFAULT_LEGAL_INFO_PATIENT_BODY
    );

    setAppointmentTitle(
      (loaded.find((x) => x.name === LEGAL_INFO_APPOINTMENT_TITLE_KEY)?.detail || "").trim() ||
        DEFAULT_LEGAL_INFO_APPOINTMENT_TITLE
    );

    setAppointmentBody(
      (loaded.find((x) => x.name === LEGAL_INFO_APPOINTMENT_BODY_KEY)?.detail || "").trim() ||
        DEFAULT_LEGAL_INFO_APPOINTMENT_BODY
    );

    setLinks(parseLinks(loaded.find((x) => x.name === LEGAL_INFO_LINKS_KEY)?.detail));
  };

  const loadDocuments = async () => {
    const docsRes = await api.get<DocumentRow[]>("/documents");
    const loadedDocs = Array.isArray(docsRes.data) ? docsRes.data : [];
    const normalized = normalizeDocuments(loadedDocs);

    setDocuments(normalized);

    setRenameMap((prev) => {
      const next = { ...prev };
      normalized.forEach((doc) => {
        const id = getDocId(doc);
        if (!next[id]) next[id] = getDocName(doc);
      });
      return next;
    });
  };

  const loadAll = async () => {
    setLoading(true);
    setErrorText(null);

    try {
      await Promise.all([loadClinicInfo(), loadDocuments()]);
    } catch (e: any) {
      setErrorText(`Ошибка загрузки: ${extractErr(e)}`);
      setRows([]);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const upsertClinicInfo = async (name: string, detail: string) => {
    const existing = rowsByKey.get(name);
    if (existing) {
      await api.delete(`/clinic-info/${existing.id}`);
    }
    await api.post("/clinic-info", { name, detail });
  };

  const saveAll = async () => {
    setLoading(true);
    setErrorText(null);

    try {
      await upsertClinicInfo(LEGAL_INFO_TITLE_KEY, safeTrim(title) || DEFAULT_LEGAL_INFO_TITLE);
      await upsertClinicInfo(LEGAL_INFO_LEAD_KEY, safeTrim(lead) || DEFAULT_LEGAL_INFO_LEAD);
      await upsertClinicInfo(
        LEGAL_INFO_PATIENT_TITLE_KEY,
        safeTrim(patientTitle) || DEFAULT_LEGAL_INFO_PATIENT_TITLE
      );
      await upsertClinicInfo(
        LEGAL_INFO_PATIENT_BODY_KEY,
        safeTrim(patientBody) || DEFAULT_LEGAL_INFO_PATIENT_BODY
      );
      await upsertClinicInfo(
        LEGAL_INFO_APPOINTMENT_TITLE_KEY,
        safeTrim(appointmentTitle) || DEFAULT_LEGAL_INFO_APPOINTMENT_TITLE
      );
      await upsertClinicInfo(
        LEGAL_INFO_APPOINTMENT_BODY_KEY,
        safeTrim(appointmentBody) || DEFAULT_LEGAL_INFO_APPOINTMENT_BODY
      );

      const cleanLinks = dedupeLinks(
        editableLinks.filter((item) => safeTrim(item.label) && safeTrim(item.href))
      );

      await upsertClinicInfo(
        LEGAL_INFO_LINKS_KEY,
        JSON.stringify(cleanLinks.length ? cleanLinks : buildResetLinks(), null, 2)
      );

      await loadClinicInfo();
    } catch (e: any) {
      setErrorText(`Ошибка сохранения: ${extractErr(e)}`);
    } finally {
      setLoading(false);
    }
  };

  const resetToDefault = () => {
    setTitle(DEFAULT_LEGAL_INFO_TITLE);
    setLead(DEFAULT_LEGAL_INFO_LEAD);
    setPatientTitle(DEFAULT_LEGAL_INFO_PATIENT_TITLE);
    setPatientBody(DEFAULT_LEGAL_INFO_PATIENT_BODY);
    setAppointmentTitle(DEFAULT_LEGAL_INFO_APPOINTMENT_TITLE);
    setAppointmentBody(DEFAULT_LEGAL_INFO_APPOINTMENT_BODY);
    setLinks(buildResetLinks());
  };

  const updateLink = (index: number, patch: Partial<LegalLinkItem>) => {
    setLinks((prev) =>
      prev.map((item, idx) =>
        idx === index
          ? {
              label: safeText(patch.label ?? item?.label),
              href: safeText(patch.href ?? item?.href),
              download: patch.download ?? Boolean(item?.download),
            }
          : {
              label: safeText(item?.label),
              href: safeText(item?.href),
              download: Boolean(item?.download),
            }
      )
    );
  };

  const addLink = () => {
    setLinks((prev) => [
      ...prev,
      {
        label: "",
        href: "",
        download: false,
      },
    ]);
  };

  const removeLink = (index: number) => {
    setLinks((prev) => prev.filter((_, idx) => idx !== index));
  };

  const applyPdfToLink = (index: number, docId: string) => {
    const cleanDocId = safeTrim(docId);

    if (!cleanDocId) {
      const currentHref = safeTrim(editableLinks[index]?.href);
      if (currentHref.startsWith("/documents/download/")) {
        updateLink(index, { href: "", download: false });
      }
      return;
    }

    const doc = validDocuments.find((x) => getDocId(x) === cleanDocId);
    if (!doc) {
      setErrorText("Не удалось найти выбранный PDF в списке документов.");
      return;
    }

    const nextHref = `/documents/download/${cleanDocId}`;
    const currentLabel = safeTrim(editableLinks[index]?.label);

    updateLink(index, {
      href: nextHref,
      download: true,
      label: currentLabel || getDocName(doc),
    });
  };

  const handleUploadDocument = async () => {
    if (!uploadFile) {
      setErrorText("Выберите PDF-файл для загрузки.");
      return;
    }

    const desiredName = safeTrim(uploadTitle) || uploadFile.name;

    setUploadingDoc(true);
    setErrorText(null);

    try {
      const fd = new FormData();
      fd.append("file", uploadFile);

      const res = await api.post<DocumentRow>("/documents/upload", fd, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      let createdDoc = res.data;

      const createdId = getDocId(createdDoc);

      if (createdId && desiredName && desiredName !== getDocName(createdDoc)) {
        const renameRes = await api.patch<DocumentRow>(`/documents/${createdId}`, {
          originalName: desiredName,
        });
        createdDoc = renameRes.data;
      }

      const created = normalizeDocuments([createdDoc]);

      if (created.length > 0) {
        setDocuments((prev) => normalizeDocuments([...prev, ...created]));

        setRenameMap((prev) => {
          const next = { ...prev };
          created.forEach((doc) => {
            const id = getDocId(doc);
            next[id] = getDocName(doc);
          });
          return next;
        });

        setPdfExpanded(true);
      } else {
        await loadDocuments();
      }

      setUploadFile(null);
      setUploadTitle("");
    } catch (e: any) {
      setErrorText(`Ошибка загрузки PDF: ${extractErr(e)}`);
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleRenameDocument = async (docId: string) => {
    const cleanDocId = safeTrim(docId);
    if (!cleanDocId) {
      setErrorText("У документа отсутствует id. Переименование невозможно.");
      return;
    }

    const nextName = safeTrim(renameMap[cleanDocId]);
    if (!nextName) {
      setErrorText("Укажите новое имя файла.");
      return;
    }

    setLoading(true);
    setErrorText(null);

    try {
      const res = await api.patch<DocumentRow>(`/documents/${cleanDocId}`, {
        originalName: nextName,
      });

      const updatedName = getDocName(res.data) || nextName;

      setDocuments((prev) =>
        prev.map((doc) =>
          getDocId(doc) === cleanDocId
            ? {
                ...doc,
                originalName: updatedName,
              }
            : doc
        )
      );

      setRenameMap((prev) => ({
        ...prev,
        [cleanDocId]: updatedName,
      }));
    } catch (e: any) {
      setErrorText(`Ошибка переименования PDF: ${extractErr(e)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    const cleanDocId = safeTrim(docId);
    if (!cleanDocId) {
      setErrorText("У документа отсутствует id. Удаление невозможно.");
      return;
    }

    if (!window.confirm("Удалить PDF-файл?")) return;

    setLoading(true);
    setErrorText(null);

    try {
      await api.delete(`/documents/${cleanDocId}`);

      setDocuments((prev) => prev.filter((doc) => getDocId(doc) !== cleanDocId));

      setRenameMap((prev) => {
        const next = { ...prev };
        delete next[cleanDocId];
        return next;
      });

      setLinks((prev) =>
        prev.filter((item) => safeTrim(item?.href) !== `/documents/download/${cleanDocId}`)
      );
    } catch (e: any) {
      setErrorText(`Ошибка удаления PDF: ${extractErr(e)}`);
    } finally {
      setLoading(false);
    }
  };

  const previewPatientBlocks = useMemo(
    () =>
      parseLegalTextBlocks(
        fillLegalTemplate(patientBody, {
          address: "ул. Примерная, 1",
          phones: "+7 (999) 111-22-33, +7 (999) 444-55-66",
        })
      ),
    [patientBody]
  );

  const previewAppointmentBlocks = useMemo(
    () =>
      parseLegalTextBlocks(
        fillLegalTemplate(appointmentBody, {
          address: "ул. Примерная, 1",
          phones: "+7 (999) 111-22-33, +7 (999) 444-55-66",
        })
      ),
    [appointmentBody]
  );

  return (
    <>
      <div className={styles.form_block}>
        <h3>Правовая информация</h3>

        {errorText ? <div style={{ color: "#c00", marginBottom: 12 }}>{errorText}</div> : null}

        <label>Заголовок страницы</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} />

        <label>Краткое описание</label>
        <textarea
          value={lead}
          onChange={(e) => setLead(e.target.value)}
          rows={3}
          style={{ width: "80%" }}
        />

        <label>Заголовок блока "Информация для пациентов"</label>
        <input value={patientTitle} onChange={(e) => setPatientTitle(e.target.value)} />

        <label>Текст блока "Информация для пациентов"</label>
        <textarea
          value={patientBody}
          onChange={(e) => setPatientBody(e.target.value)}
          rows={10}
          style={{ width: "80%", minHeight: 220 }}
        />

        <label>Заголовок блока "Адреса и телефоны контролирующих организаций"</label>
        <input value={appointmentTitle} onChange={(e) => setAppointmentTitle(e.target.value)} />

        <label>Текст блока "Адреса и телефоны контролирующих организаций"</label>
        <textarea
          value={appointmentBody}
          onChange={(e) => setAppointmentBody(e.target.value)}
          rows={12}
          style={{ width: "80%", minHeight: 260 }}
        />

        <div style={{ marginTop: 8, fontSize: 13, opacity: 0.75, maxWidth: 980 }}>
          Для списков используйте строки, начинающиеся с <b>-</b>. Можно вставлять шаблоны{" "}
          <b>{"{{address}}"}</b> и <b>{"{{phones}}"}</b>.
        </div>

        <hr style={{ margin: "20px 0" }} />

        <h3 style={{ marginTop: 0 }}>Кнопки / ссылки</h3>

        <div style={{ display: "grid", gap: 12 }}>
          {editableLinks.map((item, idx) => {
            const selectedPdfId = getPdfIdFromHref(item.href);

            return (
              <div
                key={idx}
                style={{
                  border: "1px solid #e3e3e3",
                  borderRadius: 10,
                  padding: 12,
                  background: "#fff",
                  maxWidth: 980,
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Кнопка #{idx + 1}</div>

                <label>Текст кнопки</label>
                <input
                  value={item.label}
                  onChange={(e) => updateLink(idx, { label: e.target.value })}
                  style={{ width: "70%" }}
                />

                <label>PDF из базы</label>
                <select
                  value={selectedPdfId}
                  onChange={(e) => applyPdfToLink(idx, e.target.value)}
                  style={{ width: "70%" }}
                >
                  <option value="">(не выбрано)</option>
                  {validDocuments.map((doc) => {
                    const docId = getDocId(doc);
                    const docName = getDocName(doc);

                    return (
                      <option key={docId} value={docId}>
                        {docName}
                      </option>
                    );
                  })}
                </select>

                <label>Ссылка</label>
                <input
                  value={item.href}
                  onChange={(e) => updateLink(idx, { href: e.target.value })}
                  style={{ width: "70%" }}
                  placeholder="https://... или /privacy-policy"
                />

                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(item.download)}
                    onChange={(e) => updateLink(idx, { download: e.target.checked })}
                    style={{ width: "auto", margin: 0 }}
                  />
                  Скачивать файл
                </label>

                <div style={{ marginTop: 10 }}>
                  <button
                    className={styles.btn_danger}
                    type="button"
                    onClick={() => removeLink(idx)}
                  >
                    Удалить кнопку
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
          <button className={styles.btn} type="button" onClick={addLink}>
            Добавить кнопку
          </button>

          <button className={styles.btn_primary} type="button" onClick={saveAll} disabled={loading}>
            {loading ? "Сохраняю..." : "Сохранить"}
          </button>

          <button className={styles.btn} type="button" onClick={resetToDefault} disabled={loading}>
            Сбросить к шаблону
          </button>
        </div>
      </div>

      <div className={styles.form_block}>
        <button
          type="button"
          className={styles.btn}
          onClick={() => setPdfExpanded((prev) => !prev)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: pdfExpanded ? 16 : 0,
          }}
        >
          <span>{pdfExpanded ? "▾" : "▸"}</span>
          <span>PDF-документы ({validDocuments.length})</span>
        </button>

        {pdfExpanded ? (
          <>
            <label>Выбрать PDF-файл</label>
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                setUploadFile(file);
                setUploadTitle(file?.name || "");
              }}
            />

            <label>Название PDF</label>
            <input
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              placeholder="Например: Памятка для пациентов.pdf"
              style={{ width: "70%" }}
            />

            <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
              <button
                className={styles.btn_primary}
                type="button"
                onClick={handleUploadDocument}
                disabled={uploadingDoc}
              >
                {uploadingDoc ? "Загружаю..." : "Загрузить PDF"}
              </button>
            </div>

            <div style={{ marginTop: 18, display: "grid", gap: 12 }}>
              {validDocuments.map((doc) => {
                const docId = getDocId(doc);
                const docName = getDocName(doc);
                const docMime = getDocMime(doc);
                const docSize = formatBytes(doc.size);

                return (
                  <div
                    key={docId}
                    style={{
                      border: "1px solid #e3e3e3",
                      borderRadius: 10,
                      padding: 12,
                      background: "#fff",
                      maxWidth: 980,
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>{docName}</div>

                    <div style={{ fontSize: 13, opacity: 0.72, marginBottom: 10 }}>
                      {docSize} · {docMime} · ID: {docId}
                    </div>

                    <label>Переименовать</label>
                    <input
                      value={renameMap[docId] ?? docName}
                      onChange={(e) =>
                        setRenameMap((prev) => ({
                          ...prev,
                          [docId]: e.target.value,
                        }))
                      }
                      style={{ width: "70%" }}
                    />

                    <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                      <a
                        className={styles.btn}
                        href={`/documents/download/${docId}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Открыть PDF
                      </a>

                      <button
                        className={styles.btn_primary}
                        type="button"
                        onClick={() => handleRenameDocument(docId)}
                      >
                        Сохранить имя
                      </button>

                      <button
                        className={styles.btn_danger}
                        type="button"
                        onClick={() => handleDeleteDocument(docId)}
                      >
                        Удалить PDF
                      </button>
                    </div>
                  </div>
                );
              })}

              {validDocuments.length === 0 ? (
                <div style={{ opacity: 0.7 }}>Пока PDF-файлы не загружены.</div>
              ) : null}
            </div>
          </>
        ) : null}
      </div>

      <div className={styles.form_block}>
        <h3>Предпросмотр</h3>

        <div style={{ maxWidth: 980 }}>
          <div style={{ fontSize: 32, fontWeight: 800, marginBottom: 14 }}>{title}</div>

          <div
            style={{
              marginBottom: 18,
              padding: "10px 12px",
              border: "1px solid #e3e3e3",
              borderRadius: 12,
              background: "#fff",
              lineHeight: 1.55,
            }}
          >
            {lead}
          </div>

          <div style={{ display: "grid", gap: 16 }}>
            <div
              style={{
                background: "#fff",
                border: "1px solid #e3e3e3",
                borderRadius: 12,
                padding: 16,
              }}
            >
              <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 12 }}>{patientTitle}</div>

              <div style={{ display: "grid", gap: 10 }}>
                {previewPatientBlocks.map((block, idx) =>
                  block.type === "paragraph" ? (
                    <div key={idx} style={{ lineHeight: 1.65 }}>
                      {block.text}
                    </div>
                  ) : (
                    <ul key={idx} style={{ margin: 0, paddingLeft: 20, lineHeight: 1.65 }}>
                      {block.items.map((item, itemIdx) => (
                        <li key={`${idx}_${itemIdx}`}>{item}</li>
                      ))}
                    </ul>
                  )
                )}
              </div>
            </div>

            <div
              style={{
                background: "#fff",
                border: "1px solid #e3e3e3",
                borderRadius: 12,
                padding: 16,
              }}
            >
              <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 12 }}>
                {appointmentTitle}
              </div>

              <div style={{ display: "grid", gap: 10 }}>
                {previewAppointmentBlocks.map((block, idx) =>
                  block.type === "paragraph" ? (
                    <div key={idx} style={{ lineHeight: 1.65 }}>
                      {block.text}
                    </div>
                  ) : (
                    <ul key={idx} style={{ margin: 0, paddingLeft: 20, lineHeight: 1.65 }}>
                      {block.items.map((item, itemIdx) => (
                        <li key={`${idx}_${itemIdx}`}>{item}</li>
                      ))}
                    </ul>
                  )
                )}
              </div>
            </div>

            <div
              style={{
                background: "#fff",
                border: "1px solid #e3e3e3",
                borderRadius: 12,
                padding: 16,
              }}
            >
              <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 12 }}>
                Документы и официальные ресурсы
              </div>

              <div style={{ display: "grid", gap: 10 }}>
                {normalizedLinks
                  .filter((item) => item.label && item.href)
                  .map((item, idx) => (
                    <div
                      key={`${item.label}_${idx}`}
                      style={{
                        padding: "12px 14px",
                        border: "1px solid #e3e3e3",
                        borderRadius: 10,
                        background: "#fafafa",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <img
                        src="/icons/icon_doc.svg"
                        alt=""
                        style={{ width: 20, height: 20, flex: "0 0 auto", opacity: 0.8 }}
                      />
                      <div>
                        <div style={{ fontWeight: 600 }}>{item.label}</div>
                        <div style={{ fontSize: 12, opacity: 0.7 }}>{item.href}</div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
